"use client";
import React, { memo } from "react";
import { TradeItem } from "modules/tradeLists/model/tradeList";
import { CollectionCard } from "modules/collections/details/model/collection";
import { useSets } from "modules/common/hooks/useSets";
import { SetIcon, SetPromoIndicator } from "modules/common/components/SetIcon";
import { cn } from "utils/utils";
import { AutocompleteGameCard, GameCard } from "modules/cards/model/card";
import { ShortCardSet } from "modules/common/model/models";
import type { SetIconSize } from "modules/common/presentation/gameRenderers";
import { HelpCircle, Infinity as InfinityIcon } from "lucide-react";
import Link from "next/link";
import { ICON_SIZE } from "../navigation/Menu";
import { getCatalogueSetCode } from "modules/common/gameRegistry";

interface SetTooltipProps {
  item: CollectionCard | TradeItem | GameCard | AutocompleteGameCard;
  position?: "top" | "bottom" | "left" | "right";
  size?: SetIconSize;
  iconVariant?: "default" | "chip";
}

/** Extract embedded set data from the item if available (GameCard has `set: ShortCardSet`) */
function getEmbeddedSet(
  item: CollectionCard | TradeItem | GameCard | AutocompleteGameCard
) {
  if ("set" in item && item.set) return item.set as ShortCardSet;
  return undefined;
}

function SetTooltip({
  item,
  position = "top",
  size,
  iconVariant = "default",
}: SetTooltipProps) {
  const { getSetByCode } = useSets();
  const setCode = item.setCode;
  const game = item.game;
  const embeddedSet = getEmbeddedSet(item);
  const catalogueSetCode = getCatalogueSetCode(game, {
    setCode,
    set: embeddedSet,
  });
  const set = embeddedSet ?? getSetByCode(catalogueSetCode, game);
  const isAnyPrinting = "anyPrinting" in item && item.anyPrinting;
  const isPromo =
    set?.type === "promo" ||
    ("isPromo" in item && item.isPromo === true) ||
    ("printing" in item && item.printing?.isPromo === true) ||
    ("data" in item &&
      (item.data as GameCard | undefined)?.printing?.isPromo === true);
  const isUnknownSet = !setCode;

  const tooltipContent = isAnyPrinting
    ? "Any set"
    : set?.name || setCode || "Unknown set";
  const iconClasses = "text-secondary";
  const iconContent = isAnyPrinting ? (
    <InfinityIcon size={ICON_SIZE} className={iconClasses} />
  ) : isUnknownSet ? (
    <HelpCircle size={ICON_SIZE} className={iconClasses} />
  ) : (
    <SetIcon
      setCode={setCode}
      game={game}
      size={size ?? (isPromo ? "base" : "lg")}
      variant={iconVariant}
      set={set ?? undefined}
      className={iconClasses}
    />
  );

  const setMark = (
    <SetPromoIndicator
      isPromo={isPromo}
      className="max-w-full"
      markerClassName="size-2.5"
    >
      {iconContent}
    </SetPromoIndicator>
  );
  const commonClasses = cn(
    "text-secondary hover:text-secondary focus-visible:ring-secondary/40 inline-flex min-w-0 max-w-full items-center justify-center rounded-sm outline-none focus-visible:ring-2",
    iconVariant === "chip" ? "h-8" : "h-7"
  );
  const shouldLink = !isAnyPrinting && !isUnknownSet;

  return (
    <div
      className={cn("my-1", "lg:tooltip lg:tooltip-secondary", {
        "lg:tooltip-top": position === "top",
        "lg:tooltip-bottom": position === "bottom",
        "lg:tooltip-left": position === "left",
        "lg:tooltip-right": position === "right",
      })}
      data-tip={tooltipContent}
    >
      {shouldLink ? (
        <Link
          href={`/sets/${game}/${catalogueSetCode.toLowerCase()}`}
          className={commonClasses}
          aria-label={`View all cards from ${set?.name || setCode}`}
        >
          {setMark}
        </Link>
      ) : (
        <div className={commonClasses}>{setMark}</div>
      )}
    </div>
  );
}

export default memo(SetTooltip);
